// Relance cadence — when the next follow-up is due (2026-08-12).
//
// Measured before building: 27 of 49 live orders had never been chased once,
// average 0.6 follow-ups per order, and 7 orders sat over 30 days old with 6 of
// them never touched. Nothing anywhere answered "who should I message today?",
// so chasing depended on someone remembering.
//
// This does NOT send anything. There is no WhatsApp API yet, so it drives a
// "due today" list that a human clicks through. When the 24/7 agent arrives it
// runs this exact same function — the cadence does not change, only who clicks.
//
// Guard: src/lib/followUpSchedule.test.ts

/**
 * Days to wait BEFORE each relance, measured from the previous one.
 *
 * Compounds to J+1, J+3, J+7, J+14 overall. Gaps widen on purpose: someone who
 * has ignored three messages is not persuaded by a fourth the next morning.
 */
export const RELANCE_GAP_DAYS: readonly number[] = [1, 2, 4, 7];

const DAY_MS = 86_400_000;

export interface RelanceScheduleInput {
  /** Order creation (or entry into this state) — the clock for the FIRST relance. */
  anchorAt: string;
  /** When the last relance went out, or null if none has. */
  lastSentAt: string | null;
  /** How many relances have already been sent for this state. */
  attemptsSent: number;
}

/**
 * When the next relance becomes due, or null if the ladder is finished.
 *
 * Later relances are measured from the LAST SEND, not from the order. An order
 * first chased on day 30 must not immediately owe every remaining rung at once
 * — that would fire three messages back to back and read as a malfunction.
 */
export function relanceDueAt(input: RelanceScheduleInput): Date | null {
  const sent = Math.max(0, Math.floor(input.attemptsSent || 0));
  if (sent >= RELANCE_GAP_DAYS.length) return null;   // ladder exhausted

  const gapDays = RELANCE_GAP_DAYS[sent]!;
  const fromIso = sent === 0 ? input.anchorAt : (input.lastSentAt ?? input.anchorAt);
  const from = new Date(fromIso).getTime();
  if (!Number.isFinite(from)) return null;            // bad data → never due

  return new Date(from + gapDays * DAY_MS);
}

/** Is a relance owed right now? Bad dates and finished ladders return false —
 *  failing silent is correct here, because the failure mode of the opposite is
 *  messaging a real customer for no reason. */
export function isRelanceDue(input: RelanceScheduleInput, now: Date = new Date()): boolean {
  const due = relanceDueAt(input);
  return due != null && now.getTime() >= due.getTime();
}

/** Whole days until the next relance. Negative = overdue. Null = finished. */
export function daysUntilRelance(
  input: RelanceScheduleInput,
  now: Date = new Date(),
): number | null {
  const due = relanceDueAt(input);
  if (!due) return null;
  return Math.round((due.getTime() - now.getTime()) / DAY_MS);
}
